import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Loader2, ShieldCheck, ImageOff, UserCheck, Database, Mail, Package, Megaphone, Rocket,
  CheckCircle2, XCircle, Eye, EyeOff, Pencil, RefreshCw, ExternalLink, AlertTriangle, Copy, Save, Send,
  Printer, QrCode, Download, ListChecks, RotateCcw, Palette, Users, PlayCircle,
  Trophy, TrendingUp, TrendingDown, Minus, MapPin, Globe, ClipboardList, MessageSquareText,
} from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { api, fileUrl } from '../../lib/api';
import { toast } from 'sonner';

const SectionCard = ({ icon: Icon, step, title, subtitle, children, testId }) => (
  <Card className="mb-4" data-testid={testId}>
    <CardContent className="p-4 sm:p-5">
      <div className="flex items-start gap-3 mb-3">
        <span className="h-10 w-10 rounded-xl bg-[#1a2744] flex items-center justify-center shrink-0"><Icon className="h-5 w-5 text-[#C9A84C]" /></span>
        <div>
          <p className="text-[10px] font-bold uppercase tracking-widest text-[#C9A84C]">Step {step}</p>
          <h2 className="font-serif font-black text-[#1a2744] text-lg leading-tight">{title}</h2>
          {subtitle && <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>}
        </div>
      </div>
      {children}
    </CardContent>
  </Card>
);

const CheckRow = ({ done, label, link, testId }) => (
  <div className="flex items-center gap-2.5 py-2 border-b border-slate-100 last:border-0" data-testid={testId}>
    {done ? <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0" /> : <XCircle className="h-5 w-5 text-red-500 shrink-0" />}
    <p className={`text-sm font-semibold flex-1 ${done ? 'text-slate-500' : 'text-[#1a2744]'}`}>{label}</p>
    {link && <Link to={link} className="text-xs font-bold text-[#1a2744] underline underline-offset-4 shrink-0">Open</Link>}
  </div>
);

const FLYER_OUTPUTS = [
  { key: 'letter', label: 'Letter 8.5×11', formats: ['pdf', 'png'] },
  { key: 'half', label: 'Half-page', formats: ['pdf', 'png'] },
  { key: 'square', label: 'Facebook square', formats: ['png'] },
  { key: 'yard_landscape', label: 'Yard Sign 24×18', formats: ['pdf', 'png'] },
  { key: 'yard_portrait', label: 'Yard Sign 18×24', formats: ['pdf', 'png'] },
  { key: 'yard_share', label: 'Yard Sign — Facebook/share', formats: ['png'] },
];

const CARD_OUTPUTS = [
  { key: 'card', label: 'Business Card 3.5×2 (front + back)' },
  { key: 'wallet', label: 'Wallet Card 4×2.5 (front + back)' },
];

const FLYER_TEXT_FIELDS = [
  ['title', 'Main title'],
  ['tagline', 'Tagline'],
  ['strong_line', 'Strong line / front text'],
  ['footer_line', 'Footer line / back text'],
  ['privacy_line', 'Privacy note'],
  ['disclaimer', 'Disclaimer'],
  ['facebook', 'Facebook URL'],
  ['logo_url', 'Logo (uploaded image path)'],
];

const FlyerKitSection = () => {
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState('');
  const [result, setResult] = useState(null);
  useEffect(() => { api.get('/admin/flyer/settings').then((r) => setCfg(r.data)).catch(() => {}); }, []);
  const set = (k, v) => setCfg((c) => ({ ...c, [k]: v }));

  const editablePayload = () => ({
    qr_url: cfg.qr_url, short_url: cfg.short_url,
    title: cfg.title, tagline: cfg.tagline, strong_line: cfg.strong_line, footer_line: cfg.footer_line,
    privacy_line: cfg.privacy_line, disclaimer: cfg.disclaimer, facebook: cfg.facebook, logo_url: cfg.logo_url,
  });
  const save = async () => {
    setBusy('save');
    try {
      const r = await api.put('/admin/flyer/settings', editablePayload());
      setCfg(r.data); toast.success('Flyer settings saved');
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    setBusy('');
  };
  const generate = async () => {
    setBusy('gen');
    try {
      await api.put('/admin/flyer/settings', editablePayload());
      const r = await api.post('/admin/flyer/generate', {});
      setResult(r.data);
      toast.success('Flyer + yard sign kit generated — download the print files below');
    } catch (e) { toast.error(e.response?.data?.detail || 'Generation failed'); }
    setBusy('');
  };

  if (!cfg) return null;
  return (
    <SectionCard icon={Printer} step={7} title="Print Flyer Kit" subtitle="One-page flyer + yard sign graphics with QR code for door-knocking, handouts, and lawns. Approved campaign copy and imagery only — print and share by hand." testId="launch-section-flyer">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {FLYER_TEXT_FIELDS.map(([key, label]) => (
          <div key={key}>
            <label className="block text-xs font-bold text-slate-600 mb-1">{label} (editable)</label>
            <Input value={cfg[key] || ''} onChange={(e) => set(key, e.target.value)} className="h-11" data-testid={`flyer-field-${key}`} />
          </div>
        ))}
        <div>
          <label className="block text-xs font-bold text-slate-600 mb-1 flex items-center gap-1"><QrCode className="h-3.5 w-3.5" /> QR code destination (editable)</label>
          <Input value={cfg.qr_url} onChange={(e) => set('qr_url', e.target.value)} className="h-11" data-testid="flyer-qr-url" />
          <p className="text-[11px] text-slate-400 mt-1">Update this if the final live URL changes, then regenerate.</p>
        </div>
        <div>
          <label className="block text-xs font-bold text-slate-600 mb-1">Short URL printed on the flyer</label>
          <Input value={cfg.short_url} onChange={(e) => set('short_url', e.target.value)} className="h-11" data-testid="flyer-short-url" />
        </div>
      </div>
      <div className="mt-3 rounded-lg bg-slate-50 border border-slate-200 p-3 text-xs text-slate-600" data-testid="flyer-copy-note">
        Also includes: privacy line · "{cfg.callout}" · {cfg.website} · facebook.com/VoteSteveLesperance · disclaimer: "{cfg.disclaimer}" · Ward 8 tracker logo (yard signs)
      </div>
      <div className="flex flex-wrap gap-2 mt-3">
        <Button onClick={save} disabled={!!busy} variant="outline" className="h-11 font-bold btn-press" data-testid="flyer-save-btn">
          {busy === 'save' ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />} Save Settings
        </Button>
        <Button onClick={generate} disabled={!!busy} className="h-11 bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press" data-testid="flyer-generate-btn">
          {busy === 'gen' ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Printer className="h-4 w-4 mr-1.5" />} Generate Flyer Kit
        </Button>
        <Button asChild variant="outline" className="h-11 font-bold btn-press" data-testid="flyer-kit-designs-link">
          <Link to="/admin/designs"><Palette className="h-4 w-4 mr-1.5" /> Design Manager</Link>
        </Button>
      </div>
      {result && (
        <>
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="flyer-results">
          {FLYER_OUTPUTS.map(({ key, label, formats }) => (
            <div key={key} className="rounded-xl border border-slate-200 overflow-hidden" data-testid={`flyer-output-${key}`}>
              <a href={fileUrl(result[key]?.png)} target="_blank" rel="noopener noreferrer">
                <img src={fileUrl(result[key]?.png)} alt={`${label} flyer preview`} className="w-full h-44 object-contain bg-slate-100" />
              </a>
              <div className="p-2.5">
                <p className="text-xs font-bold text-[#1a2744]">{label}</p>
                <div className="flex gap-1.5 mt-1.5">
                  {formats.map((ext) => (
                    <Button key={ext} asChild size="sm" variant="outline" className="h-8 text-xs font-bold" data-testid={`flyer-download-${key}-${ext}`}>
                      <a href={fileUrl(result[key]?.[ext])} target="_blank" rel="noopener noreferrer" download><Download className="h-3 w-3 mr-1" /> {ext.toUpperCase()}</a>
                    </Button>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="card-results">
          {CARD_OUTPUTS.map(({ key, label }) => (
            <div key={key} className="rounded-xl border border-slate-200 overflow-hidden" data-testid={`flyer-output-${key}`}>
              <div className="grid grid-cols-2 gap-1 bg-slate-100 p-1">
                <img src={fileUrl(result[key]?.front_png)} alt={`${label} front`} className="w-full h-28 object-contain" />
                <img src={fileUrl(result[key]?.back_png)} alt={`${label} back`} className="w-full h-28 object-contain" />
              </div>
              <div className="p-2.5">
                <p className="text-xs font-bold text-[#1a2744]">{label}</p>
                <div className="flex gap-1.5 mt-1.5">
                  <Button asChild size="sm" variant="outline" className="h-8 text-xs font-bold" data-testid={`flyer-download-${key}-pdf`}>
                    <a href={fileUrl(result[key]?.pdf)} target="_blank" rel="noopener noreferrer" download><Download className="h-3 w-3 mr-1" /> Print PDF (2 pages)</a>
                  </Button>
                  <Button asChild size="sm" variant="outline" className="h-8 text-xs font-bold" data-testid={`flyer-download-${key}-png`}>
                    <a href={fileUrl(result[key]?.front_png)} target="_blank" rel="noopener noreferrer" download><Download className="h-3 w-3 mr-1" /> PNG</a>
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
        </>
      )}
      <p className="text-[11px] text-slate-400 mt-3">Print files only — nothing is posted or published automatically. No restricted City Hall / CKL photos, placeholders, or map screenshots are used.</p>
    </SectionCard>
  );
};

// ---- Campaign Flyer Download settings (public download placements) ----
const FLYER_DL_FIELDS = [
  { key: 'title', label: 'Public download title' },
  { key: 'description', label: 'Short description' },
  { key: 'primary_label', label: 'PDF button label' },
  { key: 'docx_label', label: 'DOCX link label' },
  { key: 'secondary_label', label: 'Secondary label (kicker)' },
  { key: 'pdf_url', label: 'PDF file URL (print-friendly)' },
  { key: 'docx_url', label: 'DOCX file URL (Word version)' },
];

const FlyerDownloadSettingsSection = () => {
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [stats, setStats] = useState(null);
  const loadStats = () => api.get('/admin/flyer-analytics').then((r) => setStats(r.data)).catch(() => {});
  useEffect(() => { loadStats(); }, []);
  useEffect(() => {
    api.get('/admin/settings').then((r) => setCfg({
      enabled: true,
      title: 'Steve\u2019s 2-Page Campaign Flyer',
      description: 'The official 2-page campaign flyer: why Steve is running, his commitments, and how he will serve Ward 8.',
      primary_label: 'Download Steve\u2019s 2-Page Flyer PDF',
      docx_label: 'Download Steve\u2019s 2-Page Flyer DOCX',
      secondary_label: 'Steve\u2019s 2-Page Flyer',
      pdf_url: '/api/uploads/ElectSteveLesperance-2PageFlyer-01.pdf',
      docx_url: '/api/uploads/ElectSteveLesperance-2PageFlyer-01.docx',
      ...(r.data?.campaign_flyer || {}),
    })).catch(() => {});
  }, []);
  const save = async () => {
    setBusy(true);
    try {
      await api.put('/settings', { key: 'campaign_flyer', value: cfg });
      toast.success('Campaign flyer download settings saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setBusy(false);
    }
  };
  const resetStats = async () => {
    if (!window.confirm('Reset all flyer download counters to zero?')) return;
    try {
      await api.post('/admin/flyer-analytics/reset');
      toast.success('Flyer download counters reset');
      loadStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reset failed');
    }
  };
  const exportCsv = async () => {
    try {
      const r = await api.get('/admin/flyer-analytics/export', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'flyer-download-analytics.csv';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Export failed');
    }
  };
  if (!cfg) return null;
  return (
    <SectionCard icon={Download} step="7b" title="Campaign Flyer Download" subtitle="Public download of the official campaign flyer (PDF + Word). Shown on the Homepage, Meet Steve, Platform, Issue Tracker, Contact, and the site footer." testId="launch-section-flyer-download">
      <label className="flex items-center gap-2.5 text-sm font-semibold text-slate-700 mb-4">
        <Switch checked={cfg.enabled !== false} onCheckedChange={(v) => setCfg((c) => ({ ...c, enabled: v }))} data-testid="flyer-dl-enabled-switch" /> Show flyer download on the public site
      </label>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {FLYER_DL_FIELDS.map(({ key, label }) => (
          <div key={key} className={key === 'description' ? 'sm:col-span-2' : ''}>
            <label className="block text-xs font-bold text-slate-600 mb-1">{label}</label>
            <Input value={cfg[key] || ''} onChange={(e) => setCfg((c) => ({ ...c, [key]: e.target.value }))} className="h-11" data-testid={`flyer-dl-field-${key}`} />
          </div>
        ))}
      </div>
      <div className="mt-3 rounded-lg bg-slate-50 border border-slate-200 p-3 text-xs text-slate-600" data-testid="flyer-dl-note">
        Current public flyer: <strong>ElectSteveLesperance-2PageFlyer-01.pdf</strong> and <strong>ElectSteveLesperance-2PageFlyer-01.docx</strong> (Steve's 2-page flyer). Older flyer files stay stored in uploads as source material only and are no longer publicly linked. The resume is <strong>never</strong> publicly downloadable unless Steve explicitly approves it.
      </div>
      <div className="mt-4 flex flex-wrap gap-2.5">
        <Button onClick={save} disabled={busy} className="h-11 bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press" data-testid="flyer-dl-save-btn">
          {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Save className="h-4 w-4 mr-1.5" />} Save Download Settings
        </Button>
        {cfg.pdf_url && (
          <Button asChild variant="outline" className="h-11 font-bold btn-press" data-testid="flyer-dl-preview-pdf">
            <a href={fileUrl(cfg.pdf_url)} target="_blank" rel="noopener noreferrer"><ExternalLink className="h-4 w-4 mr-1.5" /> Preview PDF</a>
          </Button>
        )}
        <Button asChild variant="outline" className="h-11 font-bold btn-press" data-testid="flyer-dl-designs-link">
          <Link to="/admin/designs"><Palette className="h-4 w-4 mr-1.5" /> Upload replacement flyer</Link>
        </Button>
      </div>

      {/* Download analytics (privacy-safe aggregates only) */}
      <div className="mt-6 rounded-xl border border-slate-200 bg-slate-50/70 p-4 sm:p-5" data-testid="flyer-analytics-panel">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="font-serif font-bold text-[#1a2744]">Download Analytics</p>
          <div className="flex gap-2">
            <Button onClick={exportCsv} variant="outline" size="sm" className="h-9 font-bold" data-testid="flyer-analytics-export-btn">
              <Download className="h-3.5 w-3.5 mr-1" /> Export CSV
            </Button>
            <Button onClick={resetStats} variant="outline" size="sm" className="h-9 font-bold text-red-600 border-red-200 hover:bg-red-50" data-testid="flyer-analytics-reset-btn">
              <RotateCcw className="h-3.5 w-3.5 mr-1" /> Reset Counters
            </Button>
          </div>
        </div>
        {!stats ? (
          <p className="text-sm text-slate-500 mt-3">Loading…</p>
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
              <div className="rounded-lg bg-white border border-slate-200 p-3" data-testid="flyer-analytics-total-pdf">
                <p className="text-[11px] font-bold uppercase tracking-wide text-slate-500">PDF downloads</p>
                <p className="font-serif font-black text-2xl text-[#1a2744]">{stats.total_pdf}</p>
              </div>
              <div className="rounded-lg bg-white border border-slate-200 p-3" data-testid="flyer-analytics-total-docx">
                <p className="text-[11px] font-bold uppercase tracking-wide text-slate-500">DOCX downloads</p>
                <p className="font-serif font-black text-2xl text-[#1a2744]">{stats.total_docx}</p>
              </div>
              <div className="rounded-lg bg-white border border-slate-200 p-3" data-testid="flyer-analytics-top-page">
                <p className="text-[11px] font-bold uppercase tracking-wide text-slate-500">Top referring page</p>
                <p className="font-bold text-sm text-[#1a2744] mt-1.5">{stats.top_location || '—'}</p>
              </div>
              <div className="rounded-lg bg-white border border-slate-200 p-3" data-testid="flyer-analytics-last-download">
                <p className="text-[11px] font-bold uppercase tracking-wide text-slate-500">Last download</p>
                <p className="font-bold text-sm text-[#1a2744] mt-1.5">{stats.last_download ? `${stats.last_download.kind.toUpperCase()} · ${new Date(stats.last_download.at).toLocaleString()}` : '—'}</p>
              </div>
            </div>
            {stats.locations?.length > 0 && (
              <div className="mt-4 overflow-x-auto">
                <table className="w-full text-sm" data-testid="flyer-analytics-table">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-wide text-slate-500 border-b border-slate-200">
                      <th className="py-2 pr-3 font-bold">Page location</th>
                      <th className="py-2 pr-3 font-bold">PDF</th>
                      <th className="py-2 pr-3 font-bold">DOCX</th>
                      <th className="py-2 font-bold">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.locations.map((r) => (
                      <tr key={r.loc} className="border-b border-slate-100">
                        <td className="py-2 pr-3 font-semibold text-[#1a2744]">{r.label}</td>
                        <td className="py-2 pr-3 text-slate-700">{r.pdf}</td>
                        <td className="py-2 pr-3 text-slate-700">{r.docx}</td>
                        <td className="py-2 font-bold text-[#1a2744]">{r.total}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <p className="text-xs text-slate-500 mt-3" data-testid="flyer-analytics-privacy-note">{stats.privacy_note}</p>
            {stats.last_reset && <p className="text-xs text-slate-400 mt-1">Counters last reset: {new Date(stats.last_reset).toLocaleString()}</p>}
          </>
        )}
      </div>
    </SectionCard>
  );
};

// ---- Phase 52: QR Scan Leaderboard (aggregate-only, admin-only) ----
const TREND_META = {
  up: { icon: TrendingUp, cls: 'text-emerald-600', label: 'Up' },
  down: { icon: TrendingDown, cls: 'text-red-500', label: 'Down' },
  flat: { icon: Minus, cls: 'text-slate-400', label: 'Flat' },
};

const QrLeaderboardPanel = () => {
  const [board, setBoard] = useState(null);
  const [error, setError] = useState(false);
  const load = () => api.get('/admin/qr-leaderboard').then((r) => { setBoard(r.data); setError(false); }).catch(() => setError(true));
  useEffect(() => { load(); }, []);

  return (
    <div className="mt-6 rounded-xl border border-[#C9A84C]/40 bg-[#C9A84C]/5 p-4 sm:p-5" data-testid="qr-leaderboard-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="font-serif font-bold text-[#1a2744] flex items-center gap-2"><Trophy className="h-4 w-4 text-[#C9A84C]" /> QR Scan Leaderboard</p>
        <Button onClick={load} variant="outline" size="sm" className="h-9 font-bold" data-testid="qr-leaderboard-refresh-btn">
          <RefreshCw className="h-3.5 w-3.5 mr-1" /> Refresh
        </Button>
      </div>
      <p className="text-xs text-slate-500 mt-1">Which printed piece is driving the most scans — flyers, yard signs, cards, the window poster, and more, ranked by total scans.</p>
      {error ? (
        <div className="mt-3 rounded-lg bg-red-50 border border-red-200 p-3 flex items-center justify-between gap-2" data-testid="qr-leaderboard-error">
          <p className="text-sm text-red-800 font-semibold">Couldn't load the leaderboard.</p>
          <Button onClick={load} variant="outline" size="sm" className="h-8 text-xs font-bold">Retry</Button>
        </div>
      ) : !board ? (
        <p className="text-sm text-slate-500 mt-3">Loading…</p>
      ) : (
        <>
          {board.total === 0 && (
            <p className="text-sm text-slate-500 mt-3" data-testid="qr-leaderboard-empty">No scans recorded yet — every piece starts at zero. The ranking fills in as soon as printed QR codes get scanned.</p>
          )}
          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-sm" data-testid="qr-leaderboard-table">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-slate-500 border-b border-slate-200">
                  <th className="py-2 pr-2 font-bold">Rank</th>
                  <th className="py-2 pr-3 font-bold">Print piece</th>
                  <th className="py-2 pr-3 font-bold">Today</th>
                  <th className="py-2 pr-3 font-bold">7 days</th>
                  <th className="py-2 pr-3 font-bold">30 days</th>
                  <th className="py-2 pr-3 font-bold">Total</th>
                  <th className="py-2 pr-3 font-bold">Trend</th>
                  <th className="py-2 font-bold">Last scan</th>
                </tr>
              </thead>
              <tbody>
                {board.rows.map((r) => {
                  const T = TREND_META[r.trend] || TREND_META.flat;
                  const TIcon = T.icon;
                  return (
                    <tr key={r.source} className="border-b border-slate-100" data-testid={`qr-leaderboard-row-${r.source}`}>
                      <td className="py-2 pr-2">
                        {r.rank === 1 && r.total > 0 ? (
                          <span className="inline-flex items-center gap-1 font-black text-[#7A5C12]"><Trophy className="h-3.5 w-3.5 text-[#C9A84C]" /> 1</span>
                        ) : (
                          <span className="font-bold text-slate-500">{r.rank}</span>
                        )}
                      </td>
                      <td className="py-2 pr-3 font-semibold text-[#1a2744]">{r.label}</td>
                      <td className="py-2 pr-3 text-slate-700">{r.today}</td>
                      <td className="py-2 pr-3 text-slate-700">{r.last7}</td>
                      <td className="py-2 pr-3 text-slate-700">{r.last30}</td>
                      <td className="py-2 pr-3 font-bold text-[#1a2744]">{r.total}</td>
                      <td className="py-2 pr-3"><span className={`inline-flex items-center gap-1 text-xs font-bold ${T.cls}`} title={`${T.label} vs the prior 7 days`}><TIcon className="h-3.5 w-3.5" /> {T.label}</span></td>
                      <td className="py-2 text-xs text-slate-600 whitespace-nowrap">{r.last_scan_at ? new Date(r.last_scan_at).toLocaleString() : '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-slate-500 mt-3">All scans redirect to <span className="font-bold" data-testid="qr-leaderboard-destination">{board.destination}</span>.</p>
          <p className="text-xs text-slate-500 mt-1" data-testid="qr-leaderboard-privacy-note">{board.privacy_note}</p>
        </>
      )}
    </div>
  );
};

// ---- Phase 53: Weekly QR Snapshot (aggregate-only, admin-only) ----
const QrWeeklySnapshotPanel = () => {
  const [snap, setSnap] = useState(null);
  const [error, setError] = useState(false);
  const load = () => api.get('/admin/qr-snapshot').then((r) => { setSnap(r.data); setError(false); }).catch(() => setError(true));
  useEffect(() => { load(); }, []);

  const exportCsv = async () => {
    try {
      const r = await api.get('/admin/qr-snapshot/export', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'qr-weekly-snapshot.csv';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Export failed'); }
  };

  return (
    <div className="mt-6 rounded-xl border border-slate-200 bg-white p-4 sm:p-5" data-testid="qr-weekly-snapshot-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="font-serif font-bold text-[#1a2744] flex items-center gap-2"><TrendingUp className="h-4 w-4 text-[#C9A84C]" /> Weekly QR Snapshot</p>
        <div className="flex gap-2">
          <Button onClick={exportCsv} variant="outline" size="sm" className="h-9 font-bold" data-testid="qr-snapshot-export-btn">
            <Download className="h-3.5 w-3.5 mr-1" /> Export CSV
          </Button>
          <Button onClick={load} variant="outline" size="sm" className="h-9 font-bold" data-testid="qr-snapshot-refresh-btn">
            <RefreshCw className="h-3.5 w-3.5 mr-1" /> Refresh
          </Button>
        </div>
      </div>
      {error ? (
        <div className="mt-3 rounded-lg bg-red-50 border border-red-200 p-3 flex items-center justify-between gap-2" data-testid="qr-snapshot-error">
          <p className="text-sm text-red-800 font-semibold">Couldn't load the weekly snapshot.</p>
          <Button onClick={load} variant="outline" size="sm" className="h-8 text-xs font-bold">Retry</Button>
        </div>
      ) : !snap ? (
        <p className="text-sm text-slate-500 mt-3">Loading…</p>
      ) : (
        <>
          <div className="mt-3 rounded-lg bg-slate-50 border border-slate-200 p-3" data-testid="qr-snapshot-summary">
            <p className="text-sm font-semibold text-[#1a2744]">{snap.summary}</p>
            <p className="text-[11px] text-slate-400 mt-1">{snap.week_label}</p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3">
            <div className="rounded-lg bg-white border border-slate-200 p-3" data-testid="qr-snapshot-this-week">
              <p className="text-[11px] font-bold uppercase tracking-wide text-slate-500">This week</p>
              <p className="font-serif font-black text-2xl text-[#1a2744]">{snap.this_week_total}</p>
            </div>
            <div className="rounded-lg bg-white border border-slate-200 p-3" data-testid="qr-snapshot-last-week">
              <p className="text-[11px] font-bold uppercase tracking-wide text-slate-500">Last week</p>
              <p className="font-serif font-black text-2xl text-[#1a2744]">{snap.last_week_total}</p>
            </div>
            <div className="rounded-lg bg-white border border-slate-200 p-3" data-testid="qr-snapshot-best">
              <p className="text-[11px] font-bold uppercase tracking-wide text-slate-500">Best performer</p>
              <p className="font-bold text-sm text-emerald-700 mt-1.5">{snap.best_piece || '—'}</p>
            </div>
            <div className="rounded-lg bg-white border border-slate-200 p-3" data-testid="qr-snapshot-lowest">
              <p className="text-[11px] font-bold uppercase tracking-wide text-slate-500">Lowest performer</p>
              <p className="font-bold text-sm text-slate-600 mt-1.5">{snap.lowest_piece || '—'}</p>
            </div>
          </div>
          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-sm" data-testid="qr-snapshot-table">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-slate-500 border-b border-slate-200">
                  <th className="py-2 pr-3 font-bold">Print piece</th>
                  <th className="py-2 pr-3 font-bold">This week</th>
                  <th className="py-2 pr-3 font-bold">Last week</th>
                  <th className="py-2 pr-3 font-bold">Change</th>
                  <th className="py-2 font-bold">Total</th>
                </tr>
              </thead>
              <tbody>
                {snap.rows.map((r) => {
                  const T = TREND_META[r.direction] || TREND_META.flat;
                  const TIcon = T.icon;
                  return (
                    <tr key={r.source} className="border-b border-slate-100" data-testid={`qr-snapshot-row-${r.source}`}>
                      <td className="py-2 pr-3 font-semibold text-[#1a2744]">{r.label}</td>
                      <td className="py-2 pr-3 text-slate-700">{r.this_week}</td>
                      <td className="py-2 pr-3 text-slate-700">{r.last_week}</td>
                      <td className="py-2 pr-3">
                        <span className={`inline-flex items-center gap-1 text-xs font-bold ${T.cls}`}>
                          <TIcon className="h-3.5 w-3.5" /> {r.change > 0 ? `+${r.change}` : r.change}
                        </span>
                      </td>
                      <td className="py-2 font-bold text-[#1a2744]">{r.total}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-slate-500 mt-3" data-testid="qr-snapshot-privacy-note">{snap.privacy_note}</p>
        </>
      )}
    </div>
  );
};

// ---- QR Scan Analytics (privacy-safe aggregate scan counters) ----
const QrAnalyticsSection = () => {
  const [cfg, setCfg] = useState(null);
  const [stats, setStats] = useState(null);
  const [busy, setBusy] = useState(false);
  const loadStats = () => api.get('/admin/qr-analytics').then((r) => setStats(r.data)).catch(() => {});
  useEffect(() => {
    api.get('/admin/qr-tracking').then((r) => setCfg(r.data)).catch(() => {});
    loadStats();
  }, []);
  const save = async () => {
    setBusy(true);
    try {
      const r = await api.put('/admin/qr-tracking', { enabled: cfg.enabled !== false, base_url: cfg.base_url, destination: cfg.destination });
      setCfg((c) => ({ ...c, ...r.data }));
      toast.success('QR tracking settings saved. Regenerate the Print Flyer Kit to update printed QR codes.');
      loadStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setBusy(false);
    }
  };
  const resetStats = async () => {
    if (!window.confirm('Reset all QR scan counters to zero?')) return;
    try {
      await api.post('/admin/qr-analytics/reset');
      toast.success('QR scan counters reset');
      loadStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reset failed');
    }
  };
  const exportCsv = async () => {
    try {
      const r = await api.get('/admin/qr-analytics/export', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'qr-scan-analytics.csv';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Export failed');
    }
  };
  if (!cfg) return null;
  return (
    <SectionCard icon={QrCode} step="7c" title="QR Scan Analytics" subtitle="Privacy-safe scan counters for every printed QR code (flyers, yard signs, cards). QR codes point to a tracked link that counts the scan and sends people to your destination." testId="launch-section-qr-analytics">
      <label className="flex items-center gap-2.5 text-sm font-semibold text-slate-700 mb-4">
        <Switch checked={cfg.enabled !== false} onCheckedChange={(v) => setCfg((c) => ({ ...c, enabled: v }))} data-testid="qr-tracking-enabled-switch" /> Use tracked QR links on print materials
      </label>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-bold text-slate-600 mb-1">QR base URL (public site domain)</label>
          <Input value={cfg.base_url || ''} onChange={(e) => setCfg((c) => ({ ...c, base_url: e.target.value }))} className="h-11" data-testid="qr-tracking-base-url" />
        </div>
        <div>
          <label className="block text-xs font-bold text-slate-600 mb-1">Destination after scan (editable)</label>
          <Input value={cfg.destination || ''} onChange={(e) => setCfg((c) => ({ ...c, destination: e.target.value }))} className="h-11" placeholder="/issue-tracker" data-testid="qr-tracking-destination" />
        </div>
      </div>
      <div className="mt-3 rounded-lg bg-slate-50 border border-slate-200 p-3 text-xs text-slate-600" data-testid="qr-tracking-note">
        Printed QR codes encode <span className="font-mono font-bold">{(cfg.base_url || '').replace(/\/$/, '')}/api/qr/&#123;piece&#125;</span> which counts the scan and instantly redirects to <span className="font-bold">{cfg.destination || '/issue-tracker'}</span>. After changing these settings, regenerate the Print Flyer Kit above so the printed codes update.
      </div>
      <div className="mt-4 flex flex-wrap gap-2.5">
        <Button onClick={save} disabled={busy} className="h-11 bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press" data-testid="qr-tracking-save-btn">
          {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Save className="h-4 w-4 mr-1.5" />} Save QR Settings
        </Button>
      </div>

      {/* Scan analytics */}
      <div className="mt-6 rounded-xl border border-slate-200 bg-slate-50/70 p-4 sm:p-5" data-testid="qr-analytics-panel">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="font-serif font-bold text-[#1a2744]">Scan Analytics</p>
          <div className="flex gap-2">
            <Button onClick={exportCsv} variant="outline" size="sm" className="h-9 font-bold" data-testid="qr-analytics-export-btn">
              <Download className="h-3.5 w-3.5 mr-1" /> Export CSV
            </Button>
            <Button onClick={resetStats} variant="outline" size="sm" className="h-9 font-bold text-red-600 border-red-200 hover:bg-red-50" data-testid="qr-analytics-reset-btn">
              <RotateCcw className="h-3.5 w-3.5 mr-1" /> Reset Counters
            </Button>
          </div>
        </div>
        {!stats ? (
          <p className="text-sm text-slate-500 mt-3">Loading…</p>
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
              <div className="rounded-lg bg-white border border-slate-200 p-3" data-testid="qr-analytics-total">
                <p className="text-[11px] font-bold uppercase tracking-wide text-slate-500">Total scans</p>
                <p className="font-serif font-black text-2xl text-[#1a2744]">{stats.total}</p>
              </div>
              <div className="rounded-lg bg-white border border-slate-200 p-3" data-testid="qr-analytics-last7">
                <p className="text-[11px] font-bold uppercase tracking-wide text-slate-500">Last 7 days</p>
                <p className="font-serif font-black text-2xl text-[#1a2744]">{stats.last7}</p>
              </div>
              <div className="rounded-lg bg-white border border-slate-200 p-3" data-testid="qr-analytics-last30">
                <p className="text-[11px] font-bold uppercase tracking-wide text-slate-500">Last 30 days</p>
                <p className="font-serif font-black text-2xl text-[#1a2744]">{stats.last30}</p>
              </div>
              <div className="rounded-lg bg-white border border-slate-200 p-3" data-testid="qr-analytics-last-scan">
                <p className="text-[11px] font-bold uppercase tracking-wide text-slate-500">Last scan</p>
                <p className="font-bold text-sm text-[#1a2744] mt-1.5">{stats.last_scan ? `${stats.source_labels?.[stats.last_scan.source] || stats.last_scan.source} · ${new Date(stats.last_scan.at).toLocaleString()}` : '—'}</p>
              </div>
            </div>
            {stats.pieces?.length > 0 ? (
              <div className="mt-4 overflow-x-auto">
                <table className="w-full text-sm" data-testid="qr-analytics-table">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-wide text-slate-500 border-b border-slate-200">
                      <th className="py-2 pr-3 font-bold">Print piece</th>
                      <th className="py-2 pr-3 font-bold">Today</th>
                      <th className="py-2 pr-3 font-bold">7 days</th>
                      <th className="py-2 pr-3 font-bold">30 days</th>
                      <th className="py-2 font-bold">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.pieces.map((r) => (
                      <tr key={r.source} className="border-b border-slate-100">
                        <td className="py-2 pr-3 font-semibold text-[#1a2744]">{r.label}</td>
                        <td className="py-2 pr-3 text-slate-700">{r.today}</td>
                        <td className="py-2 pr-3 text-slate-700">{r.last7}</td>
                        <td className="py-2 pr-3 text-slate-700">{r.last30}</td>
                        <td className="py-2 font-bold text-[#1a2744]">{r.total}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-sm text-slate-500 mt-4" data-testid="qr-analytics-empty">No scans recorded yet. Counters start when someone scans a printed QR code.</p>
            )}
            <p className="text-xs text-slate-500 mt-3" data-testid="qr-analytics-privacy-note">{stats.privacy_note}</p>
            <p className="text-xs text-slate-500 mt-1">Destination: <span className="font-bold">{stats.destination}</span></p>
            {stats.last_reset && <p className="text-xs text-slate-400 mt-1">Counters last reset: {new Date(stats.last_reset).toLocaleString()}</p>}
          </>
        )}
      </div>

      {/* Phase 52: QR Scan Leaderboard */}
      <QrLeaderboardPanel />

      {/* Phase 53: Weekly QR Snapshot */}
      <QrWeeklySnapshotPanel />
    </SectionCard>
  );
};

const CHECK_STATUS = {
  not_started: { label: 'Not Started', cls: 'bg-slate-100 text-slate-600 border-slate-300' },
  in_progress: { label: 'In Progress', cls: 'bg-amber-50 text-amber-800 border-amber-300' },
  done: { label: 'Done', cls: 'bg-emerald-50 text-emerald-800 border-emerald-300' },
};

const ChecklistItem = ({ item, onUpdate }) => {
  const [note, setNote] = useState(item.note || '');
  const [showNote, setShowNote] = useState(!!item.note);
  const cycle = () => {
    const next = item.status === 'done' ? 'not_started' : item.status === 'in_progress' ? 'done' : 'done';
    onUpdate(item.id, { status: next });
  };
  const isExternal = !item.link.startsWith('/admin');
  return (
    <div className="rounded-lg border border-slate-200 p-2.5" data-testid={`checklist-item-${item.id}`}>
      <div className="flex items-center gap-2.5">
        <Checkbox checked={item.status === 'done'} onCheckedChange={cycle} className="shrink-0" data-testid={`checklist-check-${item.id}`} />
        <p className={`text-sm font-medium flex-1 min-w-0 ${item.status === 'done' ? 'text-slate-400 line-through' : 'text-[#1a2744]'}`}>{item.label}</p>
        <select
          value={item.status}
          onChange={(e) => onUpdate(item.id, { status: e.target.value })}
          className={`text-[11px] font-bold rounded-md border px-1.5 py-1 ${CHECK_STATUS[item.status].cls}`}
          data-testid={`checklist-status-${item.id}`}
        >
          {Object.entries(CHECK_STATUS).map(([v, s]) => <option key={v} value={v}>{s.label}</option>)}
        </select>
        <Button asChild size="sm" variant="ghost" className="h-8 w-8 p-0 shrink-0" title="Open" data-testid={`checklist-link-${item.id}`}>
          {isExternal
            ? <a href={item.link} target="_blank" rel="noopener noreferrer"><ExternalLink className="h-3.5 w-3.5 text-slate-400" /></a>
            : <Link to={item.link}><ExternalLink className="h-3.5 w-3.5 text-slate-400" /></Link>}
        </Button>
        <button onClick={() => setShowNote(!showNote)} className="text-[10px] font-bold text-slate-400 hover:text-[#1a2744] shrink-0" data-testid={`checklist-note-toggle-${item.id}`}>
          {showNote ? 'hide note' : 'note'}
        </button>
      </div>
      {showNote && (
        <div className="flex gap-1.5 mt-2 pl-7">
          <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a note…" className="h-8 text-xs" data-testid={`checklist-note-${item.id}`} />
          <Button size="sm" variant="outline" onClick={() => onUpdate(item.id, { note })} className="h-8 text-xs font-bold" data-testid={`checklist-note-save-${item.id}`}>Save</Button>
        </div>
      )}
    </div>
  );
};

const LaunchChecklistSection = () => {
  const [data, setData] = useState(null);
  const load = () => api.get('/admin/launch-day-checklist').then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const update = async (itemId, payload) => {
    try {
      const r = await api.put(`/admin/launch-day-checklist/item/${itemId}`, payload);
      if (r.data.all_done) toast.success('Launch checklist complete — congratulations!');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Update failed'); }
  };
  const reset = async () => {
    if (!window.confirm('Reset the whole checklist back to Not Started?')) return;
    await api.post('/admin/launch-day-checklist/reset');
    toast.success('Checklist reset');
    load();
  };

  if (!data) return null;
  const pct = data.total ? Math.round((data.done / data.total) * 100) : 0;
  return (
    <SectionCard icon={ListChecks} step={8} title="Launch Day Checklist" subtitle="Step-by-step manual launch sequence so nothing is missed. Nothing publishes or posts automatically — every step is done by hand." testId="launch-section-checklist">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <div className="h-2.5 w-44 rounded-full bg-slate-100 overflow-hidden">
            <div className="h-full bg-[#C9A84C] transition-[width] duration-200" style={{ width: `${pct}%` }} />
          </div>
          <p className="text-xs font-bold text-[#1a2744]" data-testid="checklist-progress">{data.done} / {data.total} done ({pct}%)</p>
          {data.last_completed && <p className="text-xs text-emerald-700 font-bold" data-testid="checklist-completed-date">Completed {data.last_completed.slice(0, 10)}</p>}
        </div>
        <Button size="sm" variant="outline" onClick={reset} className="h-9 text-xs font-bold text-slate-500" data-testid="checklist-reset-btn">
          <RotateCcw className="h-3.5 w-3.5 mr-1" /> Reset Checklist
        </Button>
      </div>
      <div className="space-y-4">
        {data.sections.map((sec) => {
          const secDone = sec.items.filter((i) => i.status === 'done').length;
          return (
            <div key={sec.id} data-testid={`checklist-section-${sec.id}`}>
              <p className="text-xs font-black uppercase tracking-wide text-slate-500 mb-2">{sec.title} <span className="text-slate-400 font-bold">({secDone}/{sec.items.length})</span></p>
              <div className="space-y-1.5">
                {sec.items.map((item) => <ChecklistItem key={item.id} item={item} onUpdate={update} />)}
              </div>
            </div>
          );
        })}
      </div>
    </SectionCard>
  );
};

const REVIEW_BADGE = {
  sample: 'bg-amber-50 text-amber-900 border-amber-300',
  needs_review: 'bg-[#FFF7E6] text-[#7A4E00] border-[#F2D08A]',
};

const PACKAGE_FIELDS = [
  { key: 'website_announcement', label: 'Main Website Launch Announcement' },
  { key: 'tracker_announcement', label: 'Ward 8 Public Issue Tracker Announcement' },
  { key: 'savings_announcement', label: 'Savings Tracker Announcement' },
  { key: 'ward_finder_announcement', label: 'Find Your Ward Announcement' },
  { key: 'election_announcement', label: "Who's Running / Election Centre Announcement" },
  { key: 'newsletter_draft', label: 'Newsletter Launch Draft' },
  { key: 'facebook_post', label: 'Facebook post — Main launch' },
  { key: 'facebook_tracker_post', label: 'Facebook post — Ward 8 Public Issue Tracker' },
  { key: 'facebook_pattern_post', label: 'Facebook post — Show the Pattern (Issue Tracker #2)' },
  { key: 'facebook_map_post', label: 'Facebook post — Public Issue Map' },
  { key: 'facebook_savings_post', label: 'Facebook post — Savings Tracker' },
  { key: 'facebook_ward_finder_post', label: 'Facebook post — Find Your Ward' },
  { key: 'facebook_election_post', label: 'Facebook post — Election Centre' },
  { key: 'explainer_hub', label: '"What is the Ward 8 Public Issue Tracker?" explainer' },
  { key: 'explainer_report', label: '"How to Report an Issue" explainer' },
  { key: 'explainer_map', label: '"How to Use the Ward 8 Map" explainer' },
  { key: 'explainer_election', label: '"How to Follow the Election Centre" explainer' },
  { key: 'explainer_meetings', label: '"How to follow Council meetings" explainer' },
];

const MEDIA_GROUPS = [
  { value: 'all', label: 'All' },
  { value: 'homepage', label: 'Homepage' },
  { value: 'campaign', label: 'Campaign' },
  { value: 'map', label: 'Map' },
  { value: 'election', label: 'Election' },
  { value: 'proof', label: 'Proof Archive' },
  { value: 'newsroom', label: 'Newsroom' },
];

// ---------------- Phase 50: Launch Rehearsal link + Volunteer Print Pack + QR Poster Builder ----------------

const RehearsalLinkSection = () => {
  const [state, setState] = useState(null);
  useEffect(() => { api.get('/admin/launch-rehearsal').then((r) => setState(r.data)).catch(() => {}); }, []);
  return (
    <SectionCard icon={PlayCircle} step="8b" title="Launch Rehearsal" subtitle="A complete launch-day dry run: audit, homepage, Issue Tracker, downloads, print assets, QR tracking, posts, drafts, share preview, and checklist. Nothing publishes — rehearsal only." testId="launch-section-rehearsal">
      <div className="flex flex-wrap items-center gap-3">
        <Button asChild className="h-12 bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press" data-testid="rehearsal-open-btn">
          <Link to="/admin/launch-rehearsal"><PlayCircle className="h-4 w-4 mr-2" /> Open Launch Rehearsal</Link>
        </Button>
        {state && (
          <p className="text-xs text-slate-500" data-testid="rehearsal-link-status">
            Status: <strong className="capitalize text-[#1a2744]">{state.status.replace('-', ' ')}</strong>
            {' '}· {state.reviewed_count}/{state.total_steps} reviewed
            {state.last_finished_at ? <> · last rehearsal {new Date(state.last_finished_at).toLocaleDateString()}</> : <> · never rehearsed yet</>}
          </p>
        )}
      </div>
    </SectionCard>
  );
};

const PACK_TEXTS = [
  ['talking_point_1', 'Talking point 1', 'input'],
  ['talking_point_2', 'Talking point 2', 'input'],
  ['talking_point_3', 'Talking point 3', 'input'],
  ['door_script', 'Door-knocking quick script', 'textarea'],
  ['privacy_explanation', 'Privacy explanation', 'textarea'],
  ['how_to_report', '"How to report an issue" steps (one per line)', 'textarea'],
  ['official_answer', '"Is this official City Hall?" answer', 'textarea'],
  ['facebook_link', 'Facebook page link', 'input'],
  ['website', 'Website', 'input'],
  ['disclaimer', 'Disclaimer line', 'input'],
];

const VolunteerPackSection = () => {
  const [cfg, setCfg] = useState(null);
  const [last, setLast] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState('');

  useEffect(() => {
    api.get('/admin/volunteer-pack').then((r) => { setCfg(r.data.config); setLast(r.data.last?.pdf ? r.data.last : null); }).catch(() => {});
  }, []);
  if (!cfg) return null;

  const save = async () => {
    setBusy('save');
    try { await api.put('/admin/volunteer-pack', cfg); toast.success('Volunteer pack text saved'); }
    catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
    setBusy('');
  };

  const generate = async () => {
    setBusy('gen');
    try {
      await api.put('/admin/volunteer-pack', cfg);
      const r = await api.post('/admin/volunteer-pack/generate');
      setResult(r.data); setLast(r.data);
      toast.success('Volunteer Print Pack generated');
    } catch (err) { toast.error(err.response?.data?.detail || 'Generation failed'); }
    setBusy('');
  };

  const sheet = result || last;
  return (
    <SectionCard icon={Users} step="8c" title="Volunteer Print Pack" subtitle="One ready-to-download pack for volunteers: talking points, door-knocking script, privacy explanation, how-to-report steps, the official-City-Hall answer, links, a QR handout, plus the campaign flyer, half-page flyer, and cards. Print and hand out — nothing is posted or emailed." testId="launch-section-volunteer-pack">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {PACK_TEXTS.map(([key, label, kind]) => (
          <div key={key} className={kind === 'textarea' ? 'sm:col-span-2' : ''}>
            <label className="block text-[11px] font-bold text-slate-500 mb-1">{label}</label>
            {kind === 'textarea' ? (
              <Textarea value={cfg[key] || ''} onChange={(e) => setCfg((c) => ({ ...c, [key]: e.target.value }))} rows={3} className="text-sm" data-testid={`pack-field-${key}`} />
            ) : (
              <Input value={cfg[key] || ''} onChange={(e) => setCfg((c) => ({ ...c, [key]: e.target.value }))} className="h-10" data-testid={`pack-field-${key}`} />
            )}
          </div>
        ))}
      </div>
      <div className="flex flex-wrap gap-2 mt-4">
        <Button onClick={save} disabled={!!busy} variant="outline" className="h-11 font-bold btn-press" data-testid="pack-save-btn">
          {busy === 'save' ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Save className="h-4 w-4 mr-1.5" />} Save Text
        </Button>
        <Button onClick={generate} disabled={!!busy} className="h-11 bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press" data-testid="pack-generate-btn">
          {busy === 'gen' ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Printer className="h-4 w-4 mr-1.5" />} Generate Volunteer Print Pack
        </Button>
        <Button asChild variant="outline" className="h-11 font-bold btn-press" data-testid="pack-materials-link">
          <Link to="/admin/materials"><Package className="h-4 w-4 mr-1.5" /> Material Inventory</Link>
        </Button>
      </div>
      {sheet?.pdf && (
        <div className="mt-3" data-testid="pack-result">
          <div className="flex flex-wrap items-center gap-2">
            <Button asChild variant="outline" className="h-10 font-bold btn-press" data-testid="pack-download-pdf">
              <a href={fileUrl(sheet.pdf)} target="_blank" rel="noopener noreferrer" download><Download className="h-4 w-4 mr-1.5" /> Download PDF ({sheet.pages || '–'} pages)</a>
            </Button>
            {sheet.png && (
              <Button asChild variant="outline" className="h-10 font-bold btn-press" data-testid="pack-download-png">
                <a href={fileUrl(sheet.png)} target="_blank" rel="noopener noreferrer">PNG preview</a>
              </Button>
            )}
            <p className="text-xs text-slate-400">Generated {sheet.generated_at ? new Date(sheet.generated_at).toLocaleString() : ''}</p>
          </div>
          {sheet.png && (
            <a href={fileUrl(sheet.pdf)} target="_blank" rel="noopener noreferrer" className="block mt-3 max-w-xs">
              <img src={`${fileUrl(sheet.png)}?t=${encodeURIComponent(sheet.generated_at || '')}`} alt="Volunteer pack preview (page 1)" className="w-full rounded-xl border border-slate-200 shadow-sm" data-testid="pack-preview-img" />
            </a>
          )}
          <p className="text-[11px] text-slate-400 mt-2">The pack's QR handout uses a tracked link (counts under "Other" in QR Scan Analytics) pointing to {sheet.qr_destination || 'the tracker'}.</p>
        </div>
      )}
    </SectionCard>
  );
};

const QrPosterSection = () => {
  const [data, setData] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get('/admin/qr-poster').then((r) => { setData(r.data); setCfg(r.data.config); if (r.data.last?.pdf) setResult(r.data.last); }).catch(() => {});
  }, []);
  if (!data || !cfg) return null;

  const generate = async () => {
    setBusy(true);
    try {
      const r = await api.post('/admin/qr-poster/generate', cfg);
      setResult(r.data);
      toast.success(cfg.preset === 'flyer' ? `Flyer QR Poster generated (${r.data.size_label})` : cfg.preset === 'window' ? `Window poster generated (${r.data.size_label})` : 'QR poster generated');
    } catch (err) { toast.error(err.response?.data?.detail || 'Generation failed'); }
    setBusy(false);
  };

  const isWindow = cfg.preset === 'window';
  const isFlyer = cfg.preset === 'flyer';
  const setPreset = (preset) => {
    if (preset === 'window') setCfg((c) => ({ ...data.window_defaults, size: c.size || 'letter' }));
    else if (preset === 'flyer') setCfg((c) => ({ ...data.flyer_defaults, size: c.size || 'letter' }));
    else setCfg(() => ({ ...data.defaults, size: 'letter' }));
  };

  return (
    <SectionCard icon={QrCode} step="8d" title="QR Poster Builder" subtitle="Generate a quick poster with a QR code — a standard QR poster, the Window / Community Board preset, or the Flyer QR Poster whose code opens Steve's 2-page flyer instantly. Tracked sources count in QR Scan Analytics; flyer poster scans count privacy-safely in Flyer Download Analytics." testId="launch-section-qr-poster">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="block text-[11px] font-bold text-slate-500 mb-1">Poster preset</label>
          <select value={cfg.preset || 'standard'} onChange={(e) => setPreset(e.target.value)}
            className="h-11 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm font-semibold text-[#1a2744]" data-testid="poster-preset-select">
            <option value="standard">Standard QR Poster</option>
            <option value="window">Window Poster / Community Board Poster</option>
            <option value="flyer">Flyer QR Poster (opens Steve's 2-page flyer)</option>
          </select>
        </div>
        <div>
          <label className="block text-[11px] font-bold text-slate-500 mb-1">Size</label>
          <select value={cfg.size || 'letter'} onChange={(e) => setCfg((c) => ({ ...c, size: e.target.value }))}
            disabled={!isWindow && !isFlyer}
            className="h-11 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm font-semibold text-[#1a2744] disabled:opacity-50" data-testid="poster-size-select">
            {Object.entries(data.sizes || { letter: '8.5×11 Letter' }).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          {!isWindow && !isFlyer && <p className="text-[11px] text-slate-400 mt-1">Standard poster is letter size.</p>}
        </div>
        {isFlyer && (
          <div className="sm:col-span-2">
            <p className="text-[11px] text-slate-500 bg-slate-50 border border-slate-200 rounded-lg p-2.5" data-testid="poster-flyer-note">
              Scanning this poster opens <strong>Steve's 2-Page Flyer PDF</strong> instantly. Scans count privacy-safely as "QR Poster" in Flyer Download Analytics (aggregate totals only, no personal data).
            </p>
          </div>
        )}
        {!isFlyer && (
        <div>
          <label className="block text-[11px] font-bold text-slate-500 mb-1">QR source</label>
          <select value={cfg.source} onChange={(e) => setCfg((c) => ({ ...c, source: e.target.value }))}
            className="h-11 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm font-semibold text-[#1a2744]" data-testid="poster-source-select">
            {Object.entries(data.sources).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <p className="text-[11px] text-slate-400 mt-1">{cfg.source === 'custom' ? 'Custom URLs are not tracked in QR analytics.' : cfg.source === 'window' ? `Tracked separately as "Window Poster" — lands on ${data.tracked_destination}.` : `Tracked — scans count in QR Scan Analytics and land on ${data.tracked_destination}.`}</p>
        </div>
        )}
        {!isWindow && !isFlyer && (
          <div>
            <label className="block text-[11px] font-bold text-slate-500 mb-1">Logo</label>
            <select value={cfg.logo || 'tracker'} onChange={(e) => setCfg((c) => ({ ...c, logo: e.target.value }))}
              className="h-11 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm font-semibold text-[#1a2744]" data-testid="poster-logo-select">
              <option value="tracker">PITA logo (Issue Tracker App)</option>
              <option value="campaign">Campaign logo</option>
              <option value="none">No logo</option>
            </select>
          </div>
        )}
        {cfg.source === 'custom' && (
          <div className="sm:col-span-2">
            <label className="block text-[11px] font-bold text-slate-500 mb-1">Custom destination URL (https:// only)</label>
            <Input value={cfg.custom_url || ''} onChange={(e) => setCfg((c) => ({ ...c, custom_url: e.target.value }))} placeholder="https://…" className="h-11" data-testid="poster-custom-url" />
          </div>
        )}
        <div>
          <label className="block text-[11px] font-bold text-slate-500 mb-1">Poster title</label>
          <Input value={cfg.title || ''} onChange={(e) => setCfg((c) => ({ ...c, title: e.target.value }))} className="h-11" data-testid="poster-title" />
        </div>
        <div>
          <label className="block text-[11px] font-bold text-slate-500 mb-1">Slogan</label>
          <Input value={cfg.slogan || ''} onChange={(e) => setCfg((c) => ({ ...c, slogan: e.target.value }))} className="h-11" data-testid="poster-slogan" />
        </div>
        {isWindow && (
          <>
            <div className="sm:col-span-2">
              <label className="block text-[11px] font-bold text-slate-500 mb-1">Large callout</label>
              <Input value={cfg.callout || ''} onChange={(e) => setCfg((c) => ({ ...c, callout: e.target.value }))} className="h-11" data-testid="poster-callout" />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-[11px] font-bold text-slate-500 mb-1">Privacy line</label>
              <Input value={cfg.privacy_line || ''} onChange={(e) => setCfg((c) => ({ ...c, privacy_line: e.target.value }))} className="h-11" data-testid="poster-privacy-line" />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-[11px] font-bold text-slate-500 mb-1">Facebook page</label>
              <Input value={cfg.facebook || ''} onChange={(e) => setCfg((c) => ({ ...c, facebook: e.target.value }))} className="h-11" data-testid="poster-facebook" />
            </div>
          </>
        )}
        <div className="sm:col-span-2">
          <label className="block text-[11px] font-bold text-slate-500 mb-1">Short body text</label>
          <Textarea value={cfg.body || ''} onChange={(e) => setCfg((c) => ({ ...c, body: e.target.value }))} rows={2} className="text-sm" data-testid="poster-body" />
        </div>
        <div className="sm:col-span-2">
          <label className="block text-[11px] font-bold text-slate-500 mb-1">Disclaimer</label>
          <Input value={cfg.disclaimer || ''} onChange={(e) => setCfg((c) => ({ ...c, disclaimer: e.target.value }))} className="h-11" data-testid="poster-disclaimer" />
        </div>
      </div>
      <Button onClick={generate} disabled={busy} className="h-11 mt-4 bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press" data-testid="poster-generate-btn">
        {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <QrCode className="h-4 w-4 mr-1.5" />} {isFlyer ? 'Generate Flyer QR Poster (PDF + PNG)' : isWindow ? 'Generate Window Poster (PDF + PNG)' : 'Generate PDF + PNG'}
      </Button>
      {result?.pdf && (
        <div className="mt-3" data-testid="poster-result">
          <div className="flex flex-wrap items-center gap-2">
            <Button asChild variant="outline" className="h-10 font-bold btn-press" data-testid="poster-download-pdf">
              <a href={fileUrl(result.pdf)} target="_blank" rel="noopener noreferrer" download><Download className="h-4 w-4 mr-1.5" /> Download PDF</a>
            </Button>
            {result.png && (
              <Button asChild variant="outline" className="h-10 font-bold btn-press" data-testid="poster-download-png">
                <a href={fileUrl(result.png)} target="_blank" rel="noopener noreferrer">PNG</a>
              </Button>
            )}
            <p className="text-xs text-slate-400">Generated {result.generated_at ? new Date(result.generated_at).toLocaleString() : ''}</p>
          </div>
          {result.png && (
            <a href={fileUrl(result.pdf)} target="_blank" rel="noopener noreferrer" className="block mt-3 max-w-xs">
              <img src={`${fileUrl(result.png)}?t=${encodeURIComponent(result.generated_at || '')}`} alt="QR poster preview" className="w-full rounded-xl border border-slate-200 shadow-sm" data-testid="poster-preview-img" />
            </a>
          )}
          {result.note && <p className="text-[11px] text-slate-400 mt-2" data-testid="poster-tracking-note">{result.note}</p>}
        </div>
      )}
    </SectionCard>
  );
};

// ---------------- Phase 51 Part 3: Print Review Set ----------------
const PrintReviewSection = () => {
  const [last, setLast] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get('/admin/print-review-set').then((r) => setLast(r.data.pdf ? r.data : null)).catch(() => {}); }, []);

  const generate = async () => {
    setBusy(true);
    try {
      const r = await api.post('/admin/print-review-set');
      setResult(r.data); setLast(r.data);
      toast.success(`Print Review Set generated — ${r.data.included} pieces, ${r.data.total_pages} pages`);
      if (r.data.missing?.length) toast.warning(`Missing: ${r.data.missing.join(', ')}`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Generation failed'); }
    setBusy(false);
  };

  const set = result || last;
  return (
    <SectionCard icon={ListChecks} step="8e" title="Print Review Set" subtitle="One bundle of everything for the print shop: Proof Sheet, Volunteer Print Pack, flyers, yard signs, cards, and the Window Poster — regenerated fresh and merged into a single review PDF with per-piece downloads. Review-only; nothing is published." testId="launch-section-print-review">
      <Button onClick={generate} disabled={busy} className="h-12 bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press" data-testid="review-set-generate-btn">
        {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Printer className="h-4 w-4 mr-2" />} Generate Print Review Set
      </Button>
      <Button asChild variant="outline" className="h-12 ml-2 font-bold btn-press" data-testid="review-set-materials-link">
        <Link to="/admin/materials"><Package className="h-4 w-4 mr-1.5" /> Material Inventory</Link>
      </Button>
      {busy && <p className="text-xs text-slate-400 mt-2">Regenerating proof sheet, volunteer pack, and window poster, then bundling — this can take ~20 seconds…</p>}
      {set?.pdf && (
        <div className="mt-4" data-testid="review-set-result">
          <div className="flex flex-wrap items-center gap-2">
            <Button asChild className="h-11 bg-[#C9A84C] hover:bg-[#b8973f] text-[#1a2744] font-black btn-press" data-testid="review-set-download-bundle">
              <a href={fileUrl(set.pdf)} target="_blank" rel="noopener noreferrer" download><Download className="h-4 w-4 mr-1.5" /> Download Full Bundle PDF{set.total_pages ? ` (${set.total_pages} pages)` : ''}</a>
            </Button>
            {set.generated_at && <p className="text-xs text-slate-400">Generated {new Date(set.generated_at).toLocaleString()}</p>}
          </div>
          {set.items && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-3">
              {set.items.map((it) => (
                <div key={it.file} className={`rounded-xl border p-2.5 flex items-center justify-between gap-2 ${it.exists ? 'border-slate-200' : 'border-red-200 bg-red-50/50'}`} data-testid={`review-set-item-${it.file}`}>
                  <div className="min-w-0">
                    <p className="text-xs font-bold text-[#1a2744] truncate">{it.label}</p>
                    <p className="text-[11px] text-slate-400">{it.exists ? `${it.pages} page${it.pages === 1 ? '' : 's'}` : 'Missing — generate it first'}</p>
                  </div>
                  {it.exists && (
                    <Button asChild variant="outline" size="sm" className="h-8 text-xs font-bold shrink-0" data-testid={`review-set-download-${it.file}`}>
                      <a href={fileUrl(it.url)} target="_blank" rel="noopener noreferrer"><Download className="h-3 w-3 mr-1" /> Open</a>
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
          <p className="text-[11px] text-slate-400 mt-2">{set.note}</p>
        </div>
      )}
    </SectionCard>
  );
};

const READALOUD_STATUS = {
  needs_review: { label: 'Needs Review', cls: 'bg-amber-100 text-amber-800 border-amber-300' },
  approved: { label: 'Approved', cls: 'bg-emerald-100 text-emerald-800 border-emerald-300' },
};

const ReadAloudItem = ({ item, onSave }) => {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(item.new_text || '');
  const [notes, setNotes] = useState(item.notes || '');
  const [busy, setBusy] = useState(false);
  const st = READALOUD_STATUS[item.status] || READALOUD_STATUS.needs_review;

  const save = async (patch) => {
    setBusy(true);
    try {
      await onSave(item.id, patch);
      setEditing(false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border border-slate-200 rounded-xl p-3" data-testid={`readaloud-item-${item.id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant="outline" className="font-bold text-[10px]">{item.page}</Badge>
        <span className="text-xs text-slate-500">{item.location}</span>
        <span className={`ml-auto text-[10px] font-bold px-2 py-0.5 rounded-full border ${st.cls}`} data-testid={`readaloud-status-${item.id}`}>{st.label}</span>
      </div>
      {item.old_text && (
        <p className="text-xs text-slate-400 mt-2 line-through leading-relaxed" data-testid={`readaloud-old-${item.id}`}>{item.old_text}</p>
      )}
      {editing ? (
        <div className="mt-2 space-y-2">
          <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={3} className="text-sm" data-testid={`readaloud-edit-text-${item.id}`} />
          <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} placeholder="Notes (optional)" className="text-sm" data-testid={`readaloud-edit-notes-${item.id}`} />
          <div className="flex gap-2">
            <Button size="sm" disabled={busy} onClick={() => save({ new_text: text, notes })} className="bg-[#1a2744] text-white font-bold" data-testid={`readaloud-save-${item.id}`}>
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Save className="h-3.5 w-3.5 mr-1" />} Save
            </Button>
            <Button size="sm" variant="outline" onClick={() => { setEditing(false); setText(item.new_text || ''); setNotes(item.notes || ''); }} className="font-bold">Cancel</Button>
          </div>
        </div>
      ) : (
        <>
          <p className="text-sm text-[#1a2744] mt-1.5 font-medium leading-relaxed" data-testid={`readaloud-new-${item.id}`}>{item.new_text}</p>
          {item.notes && <p className="text-xs text-slate-500 mt-1 italic">Note: {item.notes}</p>}
          <div className="flex flex-wrap gap-2 mt-2">
            {item.status !== 'approved' ? (
              <Button size="sm" disabled={busy} onClick={() => save({ status: 'approved' })} className="bg-emerald-700 hover:bg-emerald-800 text-white font-bold h-8" data-testid={`readaloud-approve-${item.id}`}>
                <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Approve
              </Button>
            ) : (
              <Button size="sm" variant="outline" disabled={busy} onClick={() => save({ status: 'needs_review' })} className="font-bold h-8" data-testid={`readaloud-unapprove-${item.id}`}>
                <RotateCcw className="h-3.5 w-3.5 mr-1" /> Back to review
              </Button>
            )}
            <Button size="sm" variant="outline" onClick={() => setEditing(true)} className="font-bold h-8" data-testid={`readaloud-edit-${item.id}`}>
              <Pencil className="h-3.5 w-3.5 mr-1" /> Edit
            </Button>
            {item.edit_link && (
              <Link to={item.edit_link} className="inline-flex items-center text-xs font-bold text-[#1a2744] underline underline-offset-4 ml-auto self-center" data-testid={`readaloud-editpage-${item.id}`}>
                Edit live wording <ExternalLink className="h-3 w-3 ml-1" />
              </Link>
            )}
          </div>
        </>
      )}
    </div>
  );
};

const ReadAloudReviewSection = () => {
  const [data, setData] = useState(null);
  const load = useCallback(() => {
    api.get('/admin/readaloud-review').then((r) => setData(r.data)).catch(() => toast.error('Could not load the copy review list'));
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveItem = async (id, patch) => {
    try {
      await api.put(`/admin/readaloud-review/item/${id}`, patch);
      toast.success('Saved');
      load();
    } catch {
      toast.error('Could not save');
      throw new Error('save failed');
    }
  };

  return (
    <SectionCard icon={MessageSquareText} step="8f" title="Read-Aloud Copy Review" subtitle="The most-changed public sentences from the natural copy cleanup. Read each one aloud, then approve it or edit the wording. Admin-only; nothing here publishes anywhere." testId="launch-section-readaloud">
      {!data ? (
        <div className="flex items-center gap-2 text-sm text-slate-500 py-4"><Loader2 className="h-4 w-4 animate-spin" /> Loading review list…</div>
      ) : (
        <>
          <div className="flex items-center gap-2 mb-3">
            <Badge className="bg-[#1a2744] text-white font-bold" data-testid="readaloud-progress">{data.approved} of {data.total} approved</Badge>
            <span className="text-xs text-slate-500">Live wording is edited on each item&apos;s own admin page; page copy fixed in code is noted for reference.</span>
          </div>
          <div className="space-y-2.5" data-testid="readaloud-list">
            {data.items.map((it) => <ReadAloudItem key={it.id} item={it} onSave={saveItem} />)}
          </div>
        </>
      )}
    </SectionCard>
  );
};

// ---- 8g. CV Download / Campaign Assets ------------------------------------
const CvDownloadAdminSection = () => {
  const [cv, setCv] = useState(null);
  const [stats, setStats] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setCv((p) => ({ ...p, [k]: v }));
  const setPlacement = (k, v) => setCv((p) => ({ ...p, placements: { ...(p.placements || {}), [k]: v } }));

  useEffect(() => {
    api.get('/admin/public-cv').then((r) => setCv(r.data)).catch(() => {});
    api.get('/admin/cv-analytics').then((r) => setStats(r.data)).catch(() => {});
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      const r = await api.put('/admin/public-cv', cv);
      setCv(r.data);
      toast.success('CV settings saved');
    } catch { toast.error('Save failed'); }
    setBusy(false);
  };

  if (!cv) return null;
  const PLACEMENTS = [
    ['meet_steve', 'Meet Steve page (Bio tab)'], ['experience', 'Experience tab'],
    ['home', 'Homepage resources area'], ['platform', 'Platform page'], ['footer', 'Site footer'],
  ];
  return (
    <SectionCard icon={Download} step="8g" title="CV Download / Campaign Assets" subtitle="Steve's approved public 2026 CV. Only these files are public; older resume files stay private. Downloads are counted with aggregate, privacy-safe counters only." testId="launch-section-cv">
      <div className="space-y-3">
        <div className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 p-3">
          <div>
            <p className="text-sm font-bold text-[#1a2744]">Show public CV download</p>
            <p className="text-xs text-slate-400">Turns every CV button on the public site on or off.</p>
          </div>
          <Switch checked={cv.enabled} onCheckedChange={(v) => set('enabled', v)} data-testid="cv-admin-enabled-toggle" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-bold text-slate-600 mb-1">CV title</label>
            <Input value={cv.title} onChange={(e) => set('title', e.target.value)} className="h-10" data-testid="cv-admin-title" />
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-600 mb-1">Button label</label>
            <Input value={cv.button_label} onChange={(e) => set('button_label', e.target.value)} className="h-10" data-testid="cv-admin-button-label" />
          </div>
        </div>
        <div>
          <label className="block text-xs font-bold text-slate-600 mb-1">Description</label>
          <Textarea rows={2} value={cv.description} onChange={(e) => set('description', e.target.value)} data-testid="cv-admin-description" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-bold text-slate-600 mb-1">DOCX file URL</label>
            <Input value={cv.docx_url} onChange={(e) => set('docx_url', e.target.value)} className="h-10" data-testid="cv-admin-docx-url" />
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-600 mb-1">PDF file URL</label>
            <Input value={cv.pdf_url} onChange={(e) => set('pdf_url', e.target.value)} className="h-10" data-testid="cv-admin-pdf-url" />
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-600 mb-1">Last updated (shown publicly)</label>
            <Input value={cv.last_updated} onChange={(e) => set('last_updated', e.target.value)} className="h-10" data-testid="cv-admin-last-updated" />
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-600 mb-1">Private notes</label>
            <Input value={cv.notes} onChange={(e) => set('notes', e.target.value)} className="h-10" data-testid="cv-admin-notes" />
          </div>
        </div>
        <div>
          <p className="text-xs font-bold text-slate-600 uppercase tracking-wide mb-2">Page placements</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {PLACEMENTS.map(([key, label]) => (
              <label key={key} className="flex items-center justify-between gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold text-[#1a2744]">
                {label}
                <Switch checked={(cv.placements || {})[key] !== false} onCheckedChange={(v) => setPlacement(key, v)} data-testid={`cv-admin-placement-${key}`} />
              </label>
            ))}
          </div>
        </div>
        {stats && (
          <p className="text-xs text-slate-500" data-testid="cv-admin-stats">
            Downloads so far: {stats.total} total ({stats.total_pdf} PDF, {stats.total_docx} DOCX). {stats.privacy_note}
          </p>
        )}
        <div className="flex flex-wrap gap-2">
          <Button onClick={save} disabled={busy} className="h-10 px-5 bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press" data-testid="cv-admin-save-btn">
            {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />} Save CV Settings
          </Button>
          <Button asChild variant="outline" className="h-10 px-4 text-xs font-bold" data-testid="cv-admin-preview-pdf">
            <a href={fileUrl(cv.pdf_url)} target="_blank" rel="noopener noreferrer">Preview PDF</a>
          </Button>
        </div>
        <p className="text-[11px] text-slate-400">New page copy (Experience intro, A Businessman With Vision, Working With Teams, Building Public-Facing Tools, and the Bio section) is editable under Admin → Pages → Meet Steve and Steve's Experience.</p>
      </div>
    </SectionCard>
  );
};

export default function AdminLaunchContent() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState('');
  const [pkg, setPkg] = useState(null);
  const [audit, setAudit] = useState(null);
  const [showAllMedia, setShowAllMedia] = useState(false);
  const [mediaGroup, setMediaGroup] = useState('all');
  const location = useLocation();

  const load = useCallback(async () => {
    const res = await api.get('/admin/launch-content');
    setData(res.data);
    setPkg(res.data.package);
  }, []);
  useEffect(() => { load(); }, [load]);

  // Scroll to a section when arriving with a #hash (e.g. from the Poster Drop List quick links)
  useEffect(() => {
    if (!data || !location.hash) return;
    const el = document.getElementById(location.hash.slice(1));
    if (el) setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 150);
  }, [data, location.hash]);

  if (!data) return <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;

  const toggleChecklist = async (key, done) => {
    await api.put('/admin/launch-content/checklist', { key, done });
    load();
  };

  const mediaAction = async (item, action) => {
    try {
      await api.post('/admin/launch-content/media-action', { collection: item.collection, id: item.id, field: item.field || '', action });
      toast.success(action === 'hide' ? 'Item hidden from the public site' : action === 'reviewed' ? 'Marked reviewed' : 'Unmarked');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Action failed');
    }
  };

  const verifyAction = async (item, action) => {
    // Guard: Mark Verified needs a source link/document OR an explicit manual note
    let manual = false;
    let note = '';
    if (action === 'verify' && !item.source_url && !item.source_doc) {
      note = window.prompt('This item has no source link or document.\n\nTo mark it MANUALLY VERIFIED, describe how it was verified (e.g. "Confirmed by phone with City Clerk\'s office, Jan 2026"). Leave empty to cancel.') || '';
      if (!note.trim()) { toast.info('Verification cancelled — add a source or a manual note.'); return; }
      manual = true;
    }
    try {
      await api.post('/admin/launch-content/verify', { collection: item.collection, id: item.id, action, manual, note });
      toast.success(action === 'verify' ? 'Marked Verified' : 'Hidden from public view');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Action failed');
    }
  };

  const generatePackage = async () => {
    setBusy('package');
    try {
      const res = await api.post('/admin/launch-content/generate-package');
      setPkg(res.data);
      toast.success('Launch package drafts generated — a newsletter draft and Facebook draft were also created');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Generation failed');
    } finally {
      setBusy('');
    }
  };

  const savePackage = async () => {
    setBusy('savepkg');
    try {
      await api.put('/admin/launch-content/package', { package: pkg });
      toast.success('Launch package saved');
    } catch {
      toast.error('Save failed');
    } finally {
      setBusy('');
    }
  };

  const copyText = async (text) => {
    try {
      await navigator.clipboard.writeText(text || '');
      toast.success('Copied to clipboard');
    } catch {
      toast.error('Copy failed — select and copy manually');
    }
  };

  const setSoftLaunch = async (patch) => {
    await api.put('/admin/launch-content/soft-launch', patch);
    toast.success('Launch settings updated');
    load();
  };

  const testEmail = async () => {
    try {
      const res = await api.post('/admin/launch-content/test-email');
      toast.success(res.data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Test failed');
    }
  };

  const runAudit = async () => {
    setBusy('audit');
    try {
      const res = await api.get('/admin/public-launch-audit');
      setAudit(res.data);
      toast[res.data.ready ? 'success' : 'warning'](res.data.verdict);
    } catch {
      toast.error('Audit failed');
    } finally {
      setBusy('');
    }
  };

  const groupedMedia = data.media.filter((m) => mediaGroup === 'all' || m.group === mediaGroup);
  const mediaItems = showAllMedia ? groupedMedia : groupedMedia.slice(0, 8);
  const pendingMedia = data.media.filter((m) => !m.reviewed).length;

  return (
    <div data-testid="admin-launch-content" className="max-w-3xl">
      <h1 className="font-serif font-black text-2xl text-[#1a2744] mb-1">Launch Content Setup</h1>
      <p className="text-sm text-slate-500 mb-5">The final human steps before the public announcement. Work top to bottom, then run the final audit.</p>

      {/* 1. Security */}
      <SectionCard icon={ShieldCheck} step={1} title="Password & Admin Security" testId="launch-section-security">
        {data.security.map((s) => <CheckRow key={s.key} done={s.done} label={s.label} link={s.link} testId={`security-check-${s.key}`} />)}
      </SectionCard>

      {/* 2. Media */}
      <SectionCard icon={ImageOff} step={2} title="Real Campaign Media" subtitle={`${data.media_count} placeholder item(s) found · ${pendingMedia} not yet reviewed`} testId="launch-section-media">
        {data.media.length === 0 ? (
          <p className="text-sm text-emerald-700 font-semibold flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4" /> No placeholder media remaining.</p>
        ) : (
          <div className="space-y-2.5">
            {/* Group filters */}
            <div className="flex gap-1.5 overflow-x-auto pb-1" data-testid="media-group-filters">
              {MEDIA_GROUPS.map((g) => (
                <button key={g.value} onClick={() => setMediaGroup(g.value)}
                  className={`h-9 px-3 rounded-full text-xs font-bold shrink-0 border ${mediaGroup === g.value ? 'bg-[#1a2744] text-white border-[#1a2744]' : 'bg-white text-[#1a2744] border-slate-300'}`}
                  data-testid={`media-filter-${g.value}`}>
                  {g.label} ({g.value === 'all' ? data.media.length : data.media.filter((m) => m.group === g.value).length})
                </button>
              ))}
            </div>
            {groupedMedia.length === 0 && <p className="text-sm text-slate-500">No placeholder items in this group.</p>}
            {mediaItems.map((m, i) => (
              <div key={`${m.collection}-${m.id}-${m.field}-${i}`} className={`border rounded-xl p-3 ${m.reviewed ? 'border-emerald-200 bg-emerald-50/40' : 'border-slate-200'}`} data-testid="launch-media-item">
                <div className="flex gap-3">
                  {m.url && m.kind === 'image' ? (
                    <img src={fileUrl(m.url)} alt="" className="h-14 w-20 object-cover rounded-lg shrink-0 bg-slate-100" />
                  ) : (
                    <span className="h-14 w-20 rounded-lg bg-slate-100 flex items-center justify-center shrink-0"><ImageOff className="h-5 w-5 text-slate-400" /></span>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-[#1a2744] leading-snug">{m.label}</p>
                    <p className="text-[11px] text-slate-500 mt-0.5" data-testid="media-item-location">Appears on: <strong>{m.page || m.collection}</strong>{m.section ? <> · Section: {m.section}</> : null}</p>
                    {m.suggested_type && <p className="text-[11px] text-slate-400 mt-0.5">Suggested: {m.suggested_type} · Recommended: {m.recommended_size}</p>}
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 mt-2.5">
                  <Button asChild variant="outline" size="sm" className="h-10 font-bold" data-testid="media-replace-button">
                    <Link to={m.edit_link || `/admin/${{ hero: 'hero', pages: 'pages', gallery: 'gallery', videos: 'videos', news: 'newsroom', stories: 'stories', page_section: 'pages' }[m.collection] || 'media-review'}`}><Pencil className="h-3.5 w-3.5 mr-1" /> Replace</Link>
                  </Button>
                  {['hero', 'gallery', 'videos', 'news', 'stories'].includes(m.collection) && (
                    <Button variant="outline" size="sm" className="h-10 font-bold" onClick={() => mediaAction(m, 'hide')} data-testid="media-hide-button"><EyeOff className="h-3.5 w-3.5 mr-1" /> Hide</Button>
                  )}
                  <Button variant="outline" size="sm" className={`h-10 font-bold ${m.reviewed ? 'border-emerald-300 text-emerald-700' : ''}`} onClick={() => mediaAction(m, m.reviewed ? 'unreviewed' : 'reviewed')} data-testid="media-reviewed-button">
                    <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> {m.reviewed ? 'Reviewed ✓' : 'Mark Reviewed'}
                  </Button>
                </div>
              </div>
            ))}
            {groupedMedia.length > 8 && (
              <button onClick={() => setShowAllMedia(!showAllMedia)} className="text-sm font-bold text-[#1a2744] underline underline-offset-4" data-testid="media-show-all">
                {showAllMedia ? 'Show fewer' : `Show all ${groupedMedia.length} items`}
              </button>
            )}
          </div>
        )}
      </SectionCard>

      {/* 3. Steve content */}
      <SectionCard icon={UserCheck} step={3} title="Steve Launch Content" subtitle="Tick each item after you have personally reviewed it." testId="launch-section-steve">
        {data.checklist.map((c) => (
          <label key={c.key} className="flex items-center gap-3 py-2 border-b border-slate-100 last:border-0 cursor-pointer" data-testid={`steve-check-${c.key}`}>
            <Switch checked={c.done} onCheckedChange={(v) => toggleChecklist(c.key, v)} />
            <span className={`text-sm font-semibold ${c.done ? 'text-slate-400 line-through' : 'text-[#1a2744]'}`}>{c.label}</span>
          </label>
        ))}
      </SectionCard>

      {/* 4. Data verification */}
      <SectionCard icon={Database} step={4} title="Data Verification" subtitle={`${data.verification_queue.length} item(s) still marked Sample or Needs Review`} testId="launch-section-verification">
        {data.verification_queue.length === 0 ? (
          <p className="text-sm text-emerald-700 font-semibold flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4" /> Everything public is verified.</p>
        ) : (
          <div className="space-y-2.5">
            <p className="text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-lg p-2.5">Only press <strong>Mark Verified</strong> after checking the figure or item against an official source. Verified items lose their "Sample" label on the public site.</p>
            {data.verification_queue.map((q) => (
              <div key={`${q.collection}-${q.id}`} className="border border-slate-200 rounded-xl p-3" data-testid="verify-item">
                <div className="flex items-start justify-between gap-2 flex-wrap">
                  <p className="text-sm font-semibold text-[#1a2744] leading-snug flex-1 min-w-0">{q.title}</p>
                  <Badge variant="outline" className={`${REVIEW_BADGE[q.status] || ''} text-[10px] font-bold uppercase shrink-0`}>{q.status === 'sample' ? 'Sample' : 'Needs Review'}</Badge>
                </div>
                <p className="text-[11px] text-slate-400 mt-0.5">
                  {q.location}
                  {q.source_url ? <a href={q.source_url} target="_blank" rel="noopener noreferrer" className="ml-2 font-bold text-[#1a2744] underline underline-offset-2 inline-flex items-center gap-0.5">Source <ExternalLink className="h-2.5 w-2.5" /></a> : q.source_doc ? <a href={q.source_doc} target="_blank" rel="noopener noreferrer" className="ml-2 font-bold text-[#1a2744] underline underline-offset-2">Source document</a> : <span className="ml-2 text-amber-700 font-semibold">No source link</span>}
                  {q.last_updated && <span className="ml-2">· Updated {q.last_updated.slice(0, 10)}</span>}
                </p>
                <div className="flex flex-wrap gap-2 mt-2.5">
                  <Button asChild variant="outline" size="sm" className="h-10 font-bold" data-testid="verify-edit-button"><Link to={q.edit_link}><Pencil className="h-3.5 w-3.5 mr-1" /> Edit</Link></Button>
                  <Button variant="outline" size="sm" className="h-10 font-bold border-emerald-300 text-emerald-700" onClick={() => verifyAction(q, 'verify')} data-testid="verify-verified-button"><CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Mark Verified</Button>
                  {q.collection !== 'tax_tracker' && (
                    <Button variant="outline" size="sm" className="h-10 font-bold" onClick={() => verifyAction(q, 'hide')} data-testid="verify-hide-button"><EyeOff className="h-3.5 w-3.5 mr-1" /> Hide</Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </SectionCard>

      {/* 5. Email */}
      <SectionCard icon={Mail} step={5} title="Email Activation" testId="launch-section-email">
        <div className="flex items-center gap-2 mb-2">
          <Badge variant="outline" className={data.email.status === 'active' ? 'bg-emerald-50 text-emerald-800 border-emerald-300 font-bold' : 'bg-amber-50 text-amber-900 border-amber-300 font-bold'} data-testid="email-status-badge">
            {data.email.status === 'active' ? 'ACTIVE' : data.email.status === 'connected_inactive' ? 'CONNECTED — NOT ACTIVATED' : 'MOCKED'}
          </Badge>
          <span className="text-xs text-slate-500">Provider: {data.email.provider}</span>
        </div>
        {data.email.status === 'mocked' && (
          <div className="flex gap-2 bg-amber-50 border border-amber-200 rounded-lg p-2.5 mb-3" data-testid="email-mocked-warning">
            <AlertTriangle className="h-4 w-4 text-amber-700 shrink-0 mt-0.5" />
            <p className="text-xs text-amber-900">Email delivery is mocked. Alert and newsletter signups are saved, but nothing is delivered until a provider (SendGrid, Mailchimp, or SMTP) is connected and activated. Public wording already reflects this honestly.</p>
          </div>
        )}
        <div className="flex flex-wrap gap-2">
          <Button asChild variant="outline" className="h-11 font-bold btn-press" data-testid="email-provider-link"><Link to="/admin/newsletter"><Mail className="h-4 w-4 mr-1.5" /> Provider Settings</Link></Button>
          {data.email.status !== 'mocked' && (
            <Button onClick={testEmail} variant="outline" className="h-11 font-bold btn-press border-[#1a2744]" data-testid="email-test-send-button"><Send className="h-4 w-4 mr-1.5" /> Test Send</Button>
          )}
        </div>
      </SectionCard>

      {/* 6. Launch package */}
      <SectionCard icon={Package} step={6} title="First Public Launch Package" subtitle="Editable drafts for the launch announcement — nothing is posted or sent automatically." testId="launch-section-package">
        <div className="mb-4 rounded-xl border border-[#C9A84C]/40 bg-[#C9A84C]/10 p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid="launch-scheduler-banner">
          <div>
            <p className="font-bold text-[#1a2744] text-sm">Plan the rollout with the Post Scheduler</p>
            <p className="text-xs text-slate-600 mt-0.5">Schedule launch posts by date and platform. Planning only — nothing is ever auto-posted.</p>
          </div>
          <Button asChild className="h-11 bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press shrink-0" data-testid="launch-scheduler-link">
            <Link to="/admin/scheduler"><Send className="h-4 w-4 mr-1.5" /> Open Post Scheduler</Link>
          </Button>
        </div>
        {!pkg ? (
          <Button onClick={generatePackage} disabled={busy === 'package'} className="h-12 w-full sm:w-auto bg-[#1a2744] text-white font-bold btn-press" data-testid="generate-package-button">
            {busy === 'package' ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Package className="h-4 w-4 mr-2" />} Generate Launch Drafts
          </Button>
        ) : (
          <div className="space-y-4">
            {PACKAGE_FIELDS.map((f) => (
              <div key={f.key} data-testid={`package-${f.key}`}>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="font-bold text-[#1a2744] text-sm">{f.label}</label>
                  <button onClick={() => copyText(pkg[f.key])} className="text-xs font-bold text-[#1a2744] inline-flex items-center gap-1 underline underline-offset-4" data-testid={`package-copy-${f.key}`}><Copy className="h-3 w-3" /> Copy</button>
                </div>
                <Textarea value={pkg[f.key] || ''} onChange={(e) => setPkg({ ...pkg, [f.key]: e.target.value })} rows={5} className="text-xs" />
              </div>
            ))}
            <div className="flex flex-wrap gap-2">
              <Button onClick={savePackage} disabled={busy === 'savepkg'} className="h-11 bg-[#1a2744] text-white font-bold btn-press" data-testid="package-save-button">
                {busy === 'savepkg' ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Save className="h-4 w-4 mr-1.5" />} Save Drafts
              </Button>
              <Button onClick={generatePackage} disabled={busy === 'package'} variant="outline" className="h-11 font-bold btn-press" data-testid="package-regenerate-button">
                <RefreshCw className="h-4 w-4 mr-1.5" /> Regenerate
              </Button>
            </div>
          </div>
        )}
      </SectionCard>

      {/* 7. Print Flyer Kit */}
      <FlyerKitSection />

      <FlyerDownloadSettingsSection />

      <QrAnalyticsSection />

      {/* 8. Launch Day Checklist */}
      <LaunchChecklistSection />

      {/* 8b-8e. Launch Rehearsal + Volunteer Print Pack + QR Poster Builder + Print Review Set */}
      <RehearsalLinkSection />
      <VolunteerPackSection />
      <QrPosterSection />
      <PrintReviewSection />

      {/* 8f. Read-Aloud Copy Review */}
      <ReadAloudReviewSection />

      {/* 8g. CV Download / Campaign Assets */}
      <CvDownloadAdminSection />

      {/* 8f. Poster Drop List (Phase 52) */}
      <SectionCard icon={MapPin} step="8f" title="Poster Drop List" subtitle="Private tracker for where posters, flyers, and cards have been placed around Ward 8 — locations, materials, permission, follow-ups, and a volunteer-friendly view. Admin-only; location details are never shown publicly." testId="launch-section-poster-drops">
        <Button asChild className="h-12 bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press" data-testid="poster-drops-open-btn">
          <Link to="/admin/poster-drops"><MapPin className="h-4 w-4 mr-2 text-[#C9A84C]" /> Open Poster Drop List</Link>
        </Button>
      </SectionCard>

      {/* 8g. Material Inventory (Phase 56) */}
      <SectionCard icon={Package} step="8g" title="Material Inventory" subtitle="Private tracker for printed campaign materials — flyers, posters, signs, and cards. See what's printed, placed, remaining, and when a reorder is needed. No automatic ordering; admin-only." testId="launch-section-materials">
        <Button asChild className="h-12 bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press" data-testid="materials-open-btn">
          <Link to="/admin/materials"><Package className="h-4 w-4 mr-2 text-[#C9A84C]" /> Open Material Inventory</Link>
        </Button>
      </SectionCard>

      {/* 8h. Door Talk Sheet (Phase 58) */}
      <SectionCard icon={Users} step="8h" title="Door Talk Sheet" subtitle="Pocket-size answer sheet for trusted volunteers — the five questions residents ask most at the door, with the tracked Door Talk QR code. Admin-only; hand distribution only." testId="launch-section-door-talk">
        <Button asChild className="h-12 bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press" data-testid="door-talk-open-btn">
          <Link to="/admin/door-talk"><Users className="h-4 w-4 mr-2 text-[#C9A84C]" /> Open Door Talk Sheet</Link>
        </Button>
      </SectionCard>

      {/* 8i. Final Launch Package (Phase 60) */}
      <SectionCard icon={Send} step="8i" title="Final Launch Package" subtitle="Everything for the www.SteveLesperance.com launch in one place — website announcement, Facebook post, four press releases, media notice, flyer download, and key links. All items stay Draft / Needs Review until Steve gives the final go." testId="launch-section-package">
        <Button asChild className="h-12 bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press" data-testid="launch-package-open-btn">
          <Link to="/admin/launch-package"><Send className="h-4 w-4 mr-2 text-[#C9A84C]" /> Open Final Launch Package</Link>
        </Button>
      </SectionCard>

      {/* 8j. Domain Activation (Phase 61) */}
      <SectionCard icon={Globe} step="8j" title="Domain Activation" subtitle="Private checklist for connecting and verifying www.SteveLesperance.com — domain, SSL, admin login, tracker, QR redirects, and share previews. Live social share tags stay OFF until Steve confirms the domain and approves activation." testId="launch-section-domain">
        <Button asChild className="h-12 bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press" data-testid="domain-activation-open-btn">
          <Link to="/admin/domain-activation"><Globe className="h-4 w-4 mr-2 text-[#C9A84C]" /> Open Domain Activation</Link>
        </Button>
      </SectionCard>

      {/* 8k. Launch Day Runbook (Phase 61) */}
      <SectionCard icon={ClipboardList} step="8k" title="Launch Day Runbook" subtitle="Printable one-page runbook with the exact order of what to approve, publish, post, and verify on launch day. Editable, human-run only — nothing publishes automatically." testId="launch-section-runbook">
        <Button asChild className="h-12 bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press" data-testid="launch-runbook-open-btn">
          <Link to="/admin/launch-runbook"><ClipboardList className="h-4 w-4 mr-2 text-[#C9A84C]" /> Open Launch Day Runbook</Link>
        </Button>
      </SectionCard>

      {/* 8l. First Response Templates (Phase 62) */}
      <SectionCard icon={MessageSquareText} step="8l" title="First Response Templates" subtitle="Ready-to-copy replies for the first issue reports, contact messages, and volunteer signups after launch. Copy and send manually — nothing is ever sent automatically." testId="launch-section-first-responses">
        <Button asChild className="h-12 bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press" data-testid="first-responses-open-btn">
          <Link to="/admin/first-responses"><MessageSquareText className="h-4 w-4 mr-2 text-[#C9A84C]" /> Open First Response Templates</Link>
        </Button>
      </SectionCard>

      {/* 9. Soft launch */}
      <SectionCard icon={Megaphone} step={9} title="Soft Launch Controls" testId="launch-section-softlaunch">
        <label className="flex items-center justify-between gap-3 py-2 border-b border-slate-100 cursor-pointer">
          <div>
            <p className="text-sm font-bold text-[#1a2744]">Soft Launch banner</p>
            <p className="text-xs text-slate-500 mt-0.5">Shows a public notice: "{data.soft_launch.banner_text}"</p>
          </div>
          <Switch checked={data.soft_launch.soft_launch_banner} onCheckedChange={(v) => setSoftLaunch({ soft_launch_banner: v })} data-testid="soft-launch-banner-toggle" />
        </label>
        <label className="flex items-center justify-between gap-3 py-2 cursor-pointer">
          <div>
            <p className="text-sm font-bold text-[#1a2744]">First-launch homepage mode</p>
            <p className="text-xs text-slate-500 mt-0.5">Shows a clean launch homepage and hides placeholder-heavy sections.</p>
          </div>
          <Switch checked={data.soft_launch.first_launch_mode} onCheckedChange={(v) => setSoftLaunch({ first_launch_mode: v })} data-testid="first-launch-mode-toggle" />
        </label>
      </SectionCard>

      {/* 10. Final audit */}
      <SectionCard icon={Rocket} step={10} title="Final Launch Audit" testId="launch-section-final">
        <Button onClick={runAudit} disabled={busy === 'audit'} className="h-12 w-full sm:w-auto bg-[#1a2744] text-white font-bold btn-press" data-testid="final-audit-button">
          {busy === 'audit' ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Rocket className="h-4 w-4 mr-2 text-[#C9A84C]" />} Run Final Launch Audit Again
        </Button>
        {audit && (
          <div className={`mt-3 rounded-xl p-4 border-2 ${audit.ready ? 'bg-emerald-50 border-emerald-300' : 'bg-red-50 border-red-300'}`} data-testid="final-audit-verdict">
            <p className={`font-black text-lg ${audit.ready ? 'text-emerald-800' : 'text-red-800'}`}>{audit.announce_verdict || audit.verdict}</p>
            <p className="text-xs text-slate-600 mt-1">{audit.blockers} blocker(s) · {audit.warnings} warning(s) · <Link to="/admin/launch" className="font-bold underline underline-offset-4">See full check details</Link></p>
          </div>
        )}
      </SectionCard>
    </div>
  );
}
